import { useCallback, useMemo } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { Megaphone01Icon } from '@hugeicons/core-solid-rounded'
import { useLingui } from '@lingui/react/macro'

import * as Table from '@/components/ui/Table'
import Loading from '@/components/ui/Loading'
import PromotionRow from './PromotionRow'
import PromotionTableHead from './PromotionTableHead'

import usePaginatedPromotions, {
  QueryVariables
} from '../hooks/usePaginatedPromotions'

function LoadingRow() {
  return (
    <Table.Row className="pointer-events-none">
      <Table.Cell colSpan={7}>
        <div className="flex flex-row justify-center p-4">
          <Loading />
        </div>
      </Table.Cell>
    </Table.Row>
  )
}

interface PromotionTableProps {
  variables: QueryVariables
  selectedIds: Set<number>
  disabledIds?: Set<number>
  hideCheckbox?: boolean
  onSelect: (promotion: GraphQL.PromotionFragmentFragment) => void
  onChangeSort: (
    field: GraphQL.PromotionsSortField,
    direction: GraphQL.SortDirection
  ) => void
}

export default function PromotionTable(props: PromotionTableProps) {
  const {
    variables,
    selectedIds,
    disabledIds,
    onSelect,
    onChangeSort,
    hideCheckbox = false
  } = props

  const { direction, field } = variables.sort

  const {
    items: promotions,
    page,
    metadata,
    loading,
    loadPage
  } = usePaginatedPromotions(variables)

  const { t } = useLingui()

  const [updatePromotion] = useMutation(AdminAPI.promotion.UPDATE)

  const { totalPages, totalEntries } = useMemo(() => {
    return {
      totalPages: metadata?.totalPages,
      totalEntries: metadata?.totalEntries
    }
  }, [metadata])

  const hasResults = useMemo(
    () => !!totalEntries && totalEntries > 0,
    [totalEntries]
  )

  const onToggleActive = useCallback(
    (id: number, newActive: boolean) => {
      updatePromotion({
        variables: {
          input: {
            id,
            isActive: newActive
          }
        }
      })
    },
    [updatePromotion]
  )

  return (
    <Table.Root>
      <PromotionTableHead
        sortDirection={direction}
        sortField={field}
        onChangeSort={onChangeSort}
        hideCheckbox={hideCheckbox}
      />
      <Table.Body>
        {promotions &&
          promotions.map(function (promotion) {
            const isDisabled = disabledIds?.has(promotion.id) ?? false

            return (
              <PromotionRow
                key={promotion.id}
                id={promotion.id}
                isDisabled={isDisabled}
                isSelected={selectedIds.has(promotion.id)}
                onSelect={() => onSelect(promotion)}
                onToggleActive={newActive =>
                  onToggleActive(promotion.id, newActive)
                }
                hideCheckbox={hideCheckbox}
              />
            )
          })}
        {!hasResults && !loading && (
          <Table.Empty
            Icon={Megaphone01Icon}
            text={t`No Promotion found`}
            to="/create-promotion"
            toText={t`Create a Promotion`}
          />
        )}
        {loading && <LoadingRow />}
      </Table.Body>
      {hasResults && page && (
        <Table.PaginationCaption
          totalEntries={totalEntries!}
          currentPage={page}
          totalPages={totalPages!}
          onChangePage={loadPage}
        />
      )}
    </Table.Root>
  )
}
